<template>
  <template v-if="isProfileView">
    <div
      class="grid-x border padding-bottom-l profile-reviews-list-item"
      :class="`margin-bottom-${isLastReview ? 'm' : 'l'}`"
    >
      <div class="cell large-1 padding-right-xs show-for-large">
        <img
          v-if="review.productData?.imgUrl"
          :src="review.productData?.imgUrl"
          :alt="`Image of ${review.productData?.name}`"
        >
        <div
          v-else
          class="placeholder-image"
        >
          <span class="picture-grey-filled" />
        </div>
      </div>
      <div class="cell large-auto">
        <div class="grid-x large-margin-bottom-z small-margin-bottom-xs">
          <div class="cell small-3 medium-2 large-1 padding-right-xs hide-for-large">
            <img
              v-if="review.productData?.imgUrl"
              :src="review.productData?.imgUrl"
              :alt="`Image of ${review.productData?.name}`"
            >
            <div
              v-else
              class="placeholder-image"
            >
              <span class="picture-grey-filled" />
            </div>
          </div>
          <div class="cell small-9 medium-10 large-auto">
            <div class="grid-x">
              <div class="cell grid-x large-5 small">
                <div class="review-stars-sm">
                  <ReviewStarsRating
                    :key="review.rating"
                    :rating="review.rating"
                    :show-rating="false"
                  />
                </div>
                <span class="margin-right-xxs">{{ timeSinceReviewSubmission }}</span>
              </div>
              <div
                v-if="review.productData?.url"
                class="cell hide-for-large margin-top-xxs margin-bottom-xxs"
              >
                <template v-if="review.productData?.available">
                  <a
                    :href="review.productData?.url"
                    target="_blank"
                    class="link-body"
                  >{{ review.productData?.productBrandAndName }}</a>
                </template>
                <template v-else>
                  <span>{{ review.productData?.productBrandAndName }}</span>
                </template>
              </div>
              <div class="cell grid-x large-auto align-right badges-section">
                <div
                  v-if="hasBadges"
                  class="badges-section grid-x shrink align-right medium-12 large-auto"
                >
                  <span
                    v-if="review.badges.incentivizedReview"
                    class="margin-bottom-xxs"
                    :class="{ 'margin-right-xs': review.badges.Staff }"
                  >
                    <BadgeBasic label="Incentivized review" />
                  </span>
                  <span
                    v-if="review.badges.Staff"
                    class="margin-bottom-xxs"
                  >
                    <BadgeBasic :label="staffBadgeText" />
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="grid-x">
          <div
            v-if="review.productData?.url"
            class="cell show-for-large margin-bottom-xs"
            :class="{ 'margin-top-xxs': !hasBadges }"
          >
            <template v-if="review.productData?.available">
              <a
                :href="review.productData?.url"
                target="_blank"
                class="link-body"
              >{{ review.productData?.productBrandAndName }}</a>
            </template>
            <template v-else>
              <span>{{ review.productData?.productBrandAndName }}</span>
            </template>
          </div>
          <div class="cell large margin-bottom-xxs">
            {{ review.title }}
          </div>
          <div class="cell">
            {{ review.reviewText }}
          </div>
          <div
            v-if="review.photos.length"
            class="cell"
          >
            <ul class="cell grid-x grid-margin-x align-middle images-container">
              <li
                v-for="image in computedPhotos"
                :key="image.id"
                class="cell small-4 large-3 margin-top-xs"
              >
                <img
                  class="uploaded-image"
                  :src="image.sizes.normal.url"
                  :alt="`Photo from ${review.userNickname}`"
                >
              </li>
            </ul>
          </div>
          <div
            v-if="hasAdditionalFields"
            class="cell margin-top-xs"
          >
            <div>
              <div
                v-for="field in additionalFields"
                :key="field.id"
                class="grid-x align-bottom additional-field"
              >
                <span class="heavy margin-right-xxs">{{ field.label }}:</span>
                <span class="small">{{ field.value }}</span>
              </div>
              <div
                v-for="field in contextDataFields"
                :key="field.id"
                class="grid-x align-bottom additional-field"
              >
                <span class="heavy-sm margin-right-xxs">{{ field.dimensionLabel }}</span>
                <span class="small">{{ field.valueLabel }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </template>

  <div
    v-else
    :id="reviewDivId"
  >
    <span
      v-if="hasValidExternalSource"
      class="source-product grid-x align-middle margin-bottom-xs"
      :class="{ 'margin-top-m': !isFullReview }"
    >
      <img
        :src="sourceProduct?.imageUrl"
        :alt="sourceProduct?.name"
        class="source-icon margin-right-xxs small-1"
      >
      <span class="external-review-source">
        This review is from
        <a
          :href="sourceProduct?.productPageUrl"
          class="link-sm"
        >{{ sourceProduct?.name }}</a>
      </span>
    </span>
    <div
      class="review-item padding-bottom-m"
      :class="{
        'border grid-x grid-margin-x': !isFullReview ,
        'margin-top-m': !hasValidExternalSource && !isFullReview,
      }"
    >
      <div
        class="cell grid-y small-12 large-3 review-item-section margin-bottom-xs"
      >
        <div
          class="grid-x shrink align-middle small"
          :class="{ 'margin-bottom-xxs': hasBadges || review.userLocation }"
        >
          <div class="review-stars-sm">
            <ReviewStarsRating
              :key="review.rating"
              :rating="review.rating"
              :show-rating="false"
            />
          </div>
          <Username
            v-if="review.userNickname"
            :user-nickname="review.userNickname"
            :author-id="review.authorId"
            :is-syndicated="review.syndicated"
            :is-employee-author="isEmployeeAuthor"
            class="margin-right-xxs"
          />
          <span class="margin-right-xxs">{{ timeSinceReviewSubmission }}</span>
        </div>
        <div
          v-if="review.userLocation"
          class="small"
          :class="{ 'margin-bottom-xxs': hasBadges }"
        >
          from {{ review.userLocation }}
        </div>
        <div
          v-if="hasBadges"
          class="grid-x shrink align-middle"
        >
          <span
            v-if="review.badges.incentivizedReview"
            class="margin-right-xs margin-bottom-xxs"
          >
            <Badge
              label="Incentivized review"
              :tooltip-body="BADGES_TOOLTIPS.incentivizedReview"
            />
          </span>
          <span
            v-if="review.badges.Staff"
            class="margin-bottom-xxs"
          >
            <Badge
              :label="staffBadgeText"
              :tooltip-body="staffBadgeTooltipBody"
            />
          </span>
        </div>
        <div
          v-if="hasAdditionalFields"
          :class="{ 'margin-top-xxs': !hasBadges }"
        >
          <div
            v-for="field in additionalFields"
            :key="field.id"
            class="grid-x align-bottom additional-field"
          >
            <span class="heavy margin-right-xxs">{{ field.label }}:</span>
            <span class="small">{{ field.value }}</span>
          </div>
          <div
            v-for="field in contextDataFields"
            :key="field.id"
            class="grid-x align-bottom additional-field"
          >
            <span class="heavy-sm margin-right-xxs">{{ field.dimensionLabel }}</span>
            <span class="small">{{ field.valueLabel }}</span>
          </div>
        </div>
        <div
          v-if="!isFullReview && secondaryRatings.length > 0"
          class="slider grid-y show-for-large"
          :class="(hasAdditionalFields || !hasBadges) ? 'margin-top-s' : 'margin-top-xs'"
        >
          <Fitslider
            v-for="(rating, index) in secondaryRatings"
            :key="rating.id"
            :labels="rating.labels"
            :title="rating.label"
            :value="rating.value"
            :value-range="rating.valueRange"
            :class="{ 'margin-top-s': index > 0 }"
          />
        </div>
      </div>
      <div class="right-side cell small-12 large-auto">
        <div class="review-item-section margin-bottom-xs">
          <h3
            v-if="review.title"
            class="margin-bottom-xxs large"
          >
            {{ review.title }}
          </h3>
          <ExpandedReviewComment
            :key="review.reviewId"
            :content="reviewText"
          />
        </div>
        <div
          v-if="secondaryRatings.length > 0"
          class="grid-y small-12 margin-bottom-xs"
          :class="{ 'hide-for-large': !isFullReview, slider: !isFullReview }"
        >
          <Fitslider
            v-for="(rating, index) in secondaryRatings"
            :key="rating.id"
            :labels="rating.labels"
            :title="rating.label"
            :value="rating.value"
            :value-range="rating.valueRange"
            :class="{ 'margin-top-xs': index > 0 }"
          />
        </div>
        <p
          v-if="review.recommended"
          class="heavy margin-bottom-xs grid-x align-middle"
        >
          Recommends this product<span
            class="checkmark-spacing"
            :class="isBcom ? 'confirmation-checkmark' : 'confirmation-black-checkmark'"
          />
        </p>
        <ul
          v-if="review.photos.length && !isFullReview"
          class="grid-x grid-margin-x align-middle images-container"
        >
          <li
            v-for="(image, index) in computedPhotos"
            :key="image.id"
            class="cell small-4 large-3 margin-bottom-xs"
          >
            <img
              class="uploaded-image"
              :src="image.sizes.normal.url"
              :alt="`Photo from ${review.userNickname}`"
              @click="displayFullReview(index)"
            >
          </li>
        </ul>
        <div v-if="review.syndicated && review.syndicationSource">
          <div class="grid-x align-middle">
            <img
              class="margin-right-xxs syndicated-logo"
              :alt="`logo for ${review.syndicationSource.name}`"
              :src="review.syndicationSource.logoImageUrl"
            >
            <p class="small syndicated-text">
              Customer review from {{ review.syndicationSource.name }}
            </p>
          </div>
        </div>
        <div
          class="review-item-section grid-x align-middle body"
          :class="{ 'margin-bottom-xs': hasResponses }"
        >
          <template v-if="!review.syndicated && isMounted">
            <button
              v-if="isBcom && !isInternationalMode"
              class="link-body comment-trigger"
              @click="toggleCommentFormVisibility"
            >
              Comment
            </button>
            <VoteButton
              :id="review.reviewId"
              :total-positive-feedback-count="review.totalPositiveFeedbackCount"
              :class="{'margin-left-s': isBcom}"
            />
          </template>
        </div>
        <div
          v-if="isCommentFormVisible && isBcom"
          class="comment-form-wrapper"
          :aria-hidden="!isCommentFormVisible"
          data-testid="comment-form-wrapper"
        >
          <CommentReviewForm
            :class="[{'grid-x max-w-708': !isFullReview},{'margin-bottom-s': (comments.length || computedClientResponses.length)}]"
            :review="review"
            @cancel-comment="toggleCommentFormVisibility"
          />
        </div>
        <ClientResponse
          v-for="response in computedClientResponses"
          :key="response.id"
          :department="response.department"
          :submission-date="response.date"
          :source-client-name="response.sourceClientName"
          :response="response.response"
          class="margin-top-xxs"
        />
        <Comment
          v-for="comment in comments"
          :id="comment.id"
          :key="comment.id"
          :author-id="comment.authorId"
          :comment-text="comment.commentText"
          :user-nickname="comment.userNickname"
          :submission-time="comment.submissionTime"
          :syndicated="comment.isSyndicated"
          :total-positive-feedback-count="comment.totalPositiveFeedbackCount"
          :badge-info="comment.badges"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, onMounted } from 'vue';
import { sanitize } from '@vue-util/sanitize';
import ReviewStarsRating from '@atomic-ui/reviewlet';
import { useMounted } from '@vueuse/core';
import { Cookie } from '@common-vue/util/src/modules/cookie';
import { capitalizeOnlyFirstWord } from '@utils/capitalize';
import Username from '@components/reviews/Username';
import ClientResponse from '@components/reviews/ClientResponse';
import Comment from '@components/reviews/Comment';
import VoteButton from '@components/reviews/VoteButton';
import Badge from '@components/reviews/Badge';
import BadgeBasic from '@components/reviews/BadgeBasic';
import Fitslider from '@components/Fitslider';
import ExpandedReviewComment from '@components/reviews/ExpandedReviewComment';
import { CommentReviewForm } from '@modules/reviewComments';
import { useBrand } from '@composables/useBrand';
import { useFeatureEligibility } from '@modules/featureControl';
import { usePageData } from '@composables/usePageData';
import { useReviews } from '@composables/reviews/useReviews';
import { useUrlLocationOperations } from '@composables/useUrlLocationOperations';
import { brandName, cookies } from 'config';
import { randomGenerator } from '@utils/commonUtils';
import { trackInViewAndViewedUCG } from '@utils/bvPixel';
import { getUserIds } from '@utils/cookiesUtils';
import { BADGES_TOOLTIPS } from '../../constants/reviews';
import { getTimeElapsed } from '../../utils/getTimeElapsed';

const props = defineProps({
  review: {
    /** @type import('vue').PropType<Review> */
    type: Object,
    required: true,
  },
  isFullReview: {
    type: Boolean,
    default: false,
  },
  isProfileView: {
    type: Boolean,
    default: false,
  },
  isLastReview: {
    type: Boolean,
    default: false,
    required: false,
  },
  isCommentFormShown: {
    type: Boolean,
    default: false,
  },
});

const reviewDivId = props.isFullReview ? `review-full-modal-${props.review.reviewId}` : `review-list-item-${props.review.reviewId}`;
const isMounted = useMounted();
onMounted(() => {
  // BV track for stats - these run for each review item loaded on the page when a review is scrolled into view
  if (!props.isProfileView) {
    trackInViewAndViewedUCG(props.review, reviewDivId);
  }
});
const { searchQuery, relatedProducts, getComments } = useReviews();
const hasBadges = computed(
  () => !!(props.review?.badges?.Staff || props.review?.badges?.incentivizedReview),
);
const { productId } = usePageData();
const { isBcom } = useBrand();
const { isSignedIn } = getUserIds();
const { isInternationalMode } = useFeatureEligibility();
const { location, redirectUrl } = useUrlLocationOperations();

/**
 * @param {string} text
 */
const wrapWithHeavy = (text) => `<span class="heavy">${text}</span>`;
const reviewText = computed(() => (searchQuery.value
  ? sanitize(props.review.reviewText).replace(
    new RegExp(searchQuery.value, 'gi'),
    wrapWithHeavy,
  )
  : sanitize(props.review.reviewText)));
// badges
const staffBadgeText = computed(() => `${brandName} employee`);

const isEmployeeAuthor = computed(() => !!props.review?.badges?.Staff);

// format staff toggletip text
const staffBadgeTooltipBody = computed(() => BADGES_TOOLTIPS.staff(brandName));

/**
 *
 * @param {*} rating
 */
const getLabels = (rating) => ({
  minLabel: rating?.minLabel,
  midLabel: rating?.midLabel,
  maxLabel: rating?.maxLabel,
});
// FIT information
const rawRatings = computed(() => props.review?.secondaryRatings || {});
const secondaryRatings = computed(() => Object.keys(rawRatings.value).map((key) => {
  const rating = rawRatings.value[key];
  const id = randomGenerator();
  return {
    ...rating,
    labels: getLabels(rating),
    id,
  };
}));

// additional fields
/** @type {import('vue').ComputedRef<{ [k:String]: any}>} */
const contextDataFields = computed(() => (props.review?.contextDataValuesOrder ?? []).map((key) => ({
  ...props.review.contextDataValues[key],
  id: randomGenerator(),
})));
/** @type {import('vue').ComputedRef<{ [k:String]: any}>} */
const additionalFields = computed(() => Object.keys(props.review?.additionalFields || {}).map((key) => ({
  ...props.review.additionalFields[key],
  label: capitalizeOnlyFirstWord(props.review.additionalFields[key].label),
  id: randomGenerator(),
})));
const hasAdditionalFields = computed(
  () => contextDataFields.value.length > 0 || additionalFields.value.length > 0,
);

const computedClientResponses = computed(
  () => props.review.clientResponses?.map((response) => {
    const id = randomGenerator();
    return {
      ...response,
      id,
    };
  }) || [],
);

const computedPhotos = computed(
  () => props.review.photos?.map((photos) => {
    const id = randomGenerator();
    return {
      ...photos,
      id,
    };
  }) || [],
);

// should add border padding?
const hasResponses = computed(
  () => props.review.commentIds?.length > 0
    || props.review.clientResponses?.length > 0,
);

const timeSinceReviewSubmission = computed(() => getTimeElapsed(props.review.submissionTime));

// Review source banner|link
const isExternalReview = computed(
  () => String(props.review.productId) !== productId,
);
const sourceProduct = computed(() => (isExternalReview.value ? relatedProducts.value[props.review.productId] : null));
const hasValidExternalSource = computed(
  () => isExternalReview.value && sourceProduct.value,
);

const comments = computed(() => getComments(props.review?.commentIds));
const emit = defineEmits(['toggleComment', 'onSelectPhoto']);

const commentFormShowLocal = ref(false);

const isCommentFormVisible = computed(() => props.isCommentFormShown && commentFormShowLocal.value);
/**
 * @param {Boolean| null} [isShownValue]
 */
const toggleCommentFormVisibility = (isShownValue = null) => {
  if (isSignedIn) {
    commentFormShowLocal.value = isShownValue ?? !commentFormShowLocal.value;
    emit('toggleComment', props.review.reviewId);
  } else {
    Cookie.set(cookies.forwardPageURL, location.href);
    redirectUrl('signIn', { redirectUrl: '/pdp/signin' });
  }
};

/**
 *
 * @param {number} selectedPhotoIndex
 */
function displayFullReview(selectedPhotoIndex) {
  emit('onSelectPhoto', props.review, selectedPhotoIndex);
}
</script>

<style lang="scss" scoped brand="mcom">
@import '@csgIcons/_confirmation-black-checkmark.scss';

.vote-button {
  border-radius: rem-calc(5);
}
</style>

<style lang="scss" scoped brand="bcom">
@import '@csgIcons/_confirmation-checkmark.scss';
@import '@csgIcons/_picture-grey-filled.scss';
.picture-grey-filled {
    @extend .picture-grey-filled;
  }
</style>

<style lang="scss" scoped brand="mcom">
@import '@csgIcons/_picture-grey.scss';
.picture-grey-filled {
    @extend .picture-grey;
  }
</style>

<style lang="scss" scoped>
@import '~settingSCSS';
@import '@csgIcons/_thumbs-up.scss';
@import '@csgIcons/_thumbs-up-selected.scss';

.syndicated-logo {
  height: 20px;
  max-width: 100px;
}

.uploaded-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  aspect-ratio: 1/1;
  cursor: pointer;
}

.placeholder-image {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: #f1f1f1;
}

.syndicated-text {
  color: $gray-1-color;
}

.checkmark-spacing {
  margin-left: rem-calc(4);
}

.slider {
  @include breakpoint(large) {
    max-width: rem-calc(300);
  }
}

.images-container li {
  max-height: rem-calc(240);
  overflow: hidden;
}

.source-product span {
  color: $gray-1-color;
}

.source-icon {
  height: rem-calc(20);
  max-width: rem-calc(20);
}

.right-side {
  overflow: hidden;
}

.border {
  border-bottom: $gray-3-border;
}

@include breakpoint(medium down) {
  .profile-summary-row,
  .badges-section {
    justify-content: unset;
  }
}

.max-w-708 {
  max-width: rem-calc(708);
}

.external-review-source {
  flex: 1;
}
</style>
